import { useAuth0 } from '@auth0/auth0-react';
import { Suspense, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { persistAuth } from 'src/features/authentication/utils/persistAuth';
import { useAppDispatch, useAppSelector } from 'src/hooks/hooks';
import { IUserSlice } from 'src/store/user/userSlice.contracts';

export default function AuthGuard({ children }: { children: JSX.Element }) {
    try {
        const userSlice: IUserSlice = useAppSelector((state) => state.user);
        const location = useLocation();
        const auth = useAuth0();
        const navigate = useNavigate()
        const dispatch = useAppDispatch()

        useEffect(() => {
            // console.log(auth)
            const prep = async () => {
                try {
                    const tokens = await auth.getAccessTokenSilently()
                    const userSlice: IUserSlice = {
                        user: auth.user,
                        status: 'authenticated',
                        accessToken: tokens,
                        refreshToken: null,
                        error: null,
                    }
                    dispatch(persistAuth({ userAuth: userSlice }))
                } catch (error) {
                    console.error(error)
                    toast.error(
                        'There has been an error getting authentication details. Please try logging in',
                    );
                    navigate('/auth/login')
                }
            };
            if (!auth.isLoading && auth.user) {
                prep();
            }

            if (auth.error) {
                console.error(auth.error)
                toast.error(
                    'There has been an error getting authentication details. Please try logging in',
                );
                navigate('/auth/login')
            }
        }, [auth.user, auth.isLoading, auth.error]);

        if (auth.isLoading) {
            return <Suspense fallback={<SuspenseLoader />}>
                <SuspenseLoader />
            </Suspense>
        }

        if (!auth.isAuthenticated && !auth.user && !auth.isLoading) {
            // Redirect users to the login page, but save the current location they were
            // trying to go to when they were redirected. This allows us to send them
            // along to that page after they login, which is a nicer user experience
            // than dropping them off on the home page.
            if (!userSlice || !userSlice?.user) {
                return (<Navigate to='/auth/login' state={{ from: location }} replace />);
            }
        }

        if (!auth.isLoading && !auth.user?.email_verified) {
            return (<Navigate to='/status/unverified' state={{ from: location }} replace />);
        }
    } catch (error) {
        //Route to page if anything failed here
        return (<Navigate to='/status/500' replace />);
    }


    return children;
};